import { User } from '@/api/models';
import { AdvancedTable } from '@/components/ui/AdvancedTable';
import { Button } from '@/components/ui/Button';
import { CardBox } from '@/components/ui/CardBox';
import { getRoleLabel } from '@/lib/roles';
import { mdiDelete, mdiEye, mdiPencil } from '@mdi/js';
import * as React from 'react';

export interface UsersTableProps {
  users: User[];
  isLoading: boolean;
  sortBy: string;
  sortOrder: 'asc' | 'desc';
  onSort: (key: string) => void;
  onRowClick: (user: User) => void;
  onEditUser: (user: User) => void;
  onDeleteUser: (user: User) => void;
  isDeleting: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  isLoading,
  sortBy,
  sortOrder,
  onSort,
  onRowClick,
  onEditUser,
  onDeleteUser,
  isDeleting,
}) => {
  const columns = [
    { key: 'avatar', label: '', sortable: false, className: 'w-14' },
    { key: 'name', label: 'Name', sortable: true },
    { key: 'role', label: 'Role', sortable: false, className: 'w-48' },
    { key: 'status', label: 'Status', sortable: false, className: 'w-24' },
    { key: 'createdAt', label: 'Created', sortable: true, className: 'w-32' },
  ];

  const renderCell = (
    key: string,
    value: string | number | boolean | string[] | undefined,
    item: User,
  ): React.ReactNode => {
    switch (key) {
      case 'avatar':
        return (
          <div className='w-10 h-10 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white text-sm font-semibold'>
            {item.firstName?.[0]}
            {item.lastName?.[0]}
          </div>
        );
      case 'name':
        return (
          <div>
            <div className='font-medium text-gray-900'>
              {item.firstName} {item.lastName}
            </div>
            <div className='text-sm text-gray-500'>ID: {item.id}</div>
          </div>
        );
      case 'role':
        return (
          <div className='flex flex-wrap gap-1'>
            {item.roles && item.roles.length > 0 ? (
              item.roles.map((role, index) => (
                <span
                  key={index}
                  className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700'
                >
                  {getRoleLabel(role)}
                </span>
              ))
            ) : (
              <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700'>
                User
              </span>
            )}
          </div>
        );
      case 'status':
        return item.enabled !== false ? (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700'>
            Active
          </span>
        ) : (
          <span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700'>
            Disabled
          </span>
        );
      case 'createdAt':
        return (
          <div className='text-sm text-gray-900'>
            {item.createdAt
              ? new Date(item.createdAt).toLocaleDateString()
              : 'N/A'}
          </div>
        );
      default:
        return String(value || '-');
    }
  };

  const renderActions = (user: User) => (
    <div className='flex items-center space-x-2'>
      <Button
        icon={mdiEye}
        small
        color='info'
        title='View Details'
        onClick={() => onRowClick(user)}
      />
      <Button
        icon={mdiPencil}
        small
        color='warning'
        title='Edit User'
        onClick={() => onEditUser(user)}
      />
      <Button
        icon={mdiDelete}
        small
        color='danger'
        title='Delete User'
        loading={isDeleting}
        onClick={() => onDeleteUser(user)}
      />
    </div>
  );

  return (
    <CardBox hasTable>
      <AdvancedTable
        columns={columns}
        data={users}
        loading={isLoading}
        onSort={onSort}
        sortKey={sortBy}
        sortDirection={sortOrder}
        renderCell={renderCell}
        renderActions={renderActions}
        onRowClick={onRowClick}
        emptyMessage='No users found. Try adjusting your search criteria.'
      />
    </CardBox>
  );
};
